/**
 * parseNavigationMarkers
 *
 * Splits an assistant message string into an array of segments — alternating
 * between plain text, navigation markers, and external link markers.  The
 * frontend can map over the result and render each segment type differently.
 *
 * Marker syntax:
 *   [NAV:/some/path|Display Text]     — in-app navigation (SPA route)
 *   [EXT:https://some-url|Display Text] — external link (opens in new tab)
 *
 * Example:
 *   "Go to [NAV:/settings/subscription|Subscription Settings] or read our [EXT:https://www.typehero.ai/privacy-policy|Privacy Policy]."
 *   =>
 *   [
 *     { type: 'text',          content: 'Go to ' },
 *     { type: 'navigation',    path: '/settings/subscription', label: 'Subscription Settings' },
 *     { type: 'text',          content: ' or read our ' },
 *     { type: 'external_link', url: 'https://www.typehero.ai/privacy-policy', label: 'Privacy Policy' },
 *     { type: 'text',          content: '.' },
 *   ]
 */

interface TextSegment {
  type: 'text';
  content: string;
}

interface NavigationSegment {
  type: 'navigation';
  path: string;
  label: string;
}

interface ExternalLinkSegment {
  type: 'external_link';
  url: string;
  label: string;
}

type MessageSegment = TextSegment | NavigationSegment | ExternalLinkSegment;

// Combined regex that matches both [NAV:/path|label] and [EXT:https://url|label]
// Uses alternation with named-style groups to distinguish the two marker types.
const MARKER_REGEX = /\[NAV:(\/[^\]|]+)\|([^\]]+)\]|\[EXT:(https?:\/\/[^\]|]+)\|([^\]]+)\]/g;

export function parseNavigationMarkers(text: string): MessageSegment[] {
  const segments: MessageSegment[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  MARKER_REGEX.lastIndex = 0;

  while ((match = MARKER_REGEX.exec(text)) !== null) {
    if (match.index > lastIndex) {
      segments.push({
        type: 'text',
        content: text.slice(lastIndex, match.index),
      });
    }

    if (match[1] !== undefined) {
      // NAV marker: match[1] = path, match[2] = label
      segments.push({
        type: 'navigation',
        path: match[1],
        label: match[2],
      });
    } else {
      // EXT marker: match[3] = url, match[4] = label
      segments.push({
        type: 'external_link',
        url: match[3],
        label: match[4],
      });
    }

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    segments.push({
      type: 'text',
      content: text.slice(lastIndex),
    });
  }

  if (segments.length === 0 && text.length > 0) {
    segments.push({ type: 'text', content: text });
  }

  return segments;
}
